import {Injectable} from '@angular/core';
import {forkJoin, Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {
    VisionProviderDiagnostics,
    VisionProviderSubscription,
    VisionProviderSummary,
    VisionProviderSyncReport,
    VisionSubscriptionConsistencyReport,
    VisionSubscriptionRemoteComparison,
} from '../../models/vision-provider-management';
import {MetadataEntry} from '../vte/vehicle-transaction-view.service';
import {VisionProviderSubscriptionApiService} from './vision-provider-subscription-api.service';

export type AuthenticationSummaryTone = 'connected' | 'not_connected' | 'auth_required';

/** Consistency inputs for Diagnostics primary Synchronization Status. */
export type ConsistencySyncStatusContext = {
    report: VisionSubscriptionConsistencyReport | null;
    loadFailed: boolean;
    /** True after the consistency request succeeds or fails (not while still pending). */
    resolved: boolean;
};

/** Compact Authentication-page summary derived from existing diagnostics only. */
export type AuthenticationSummaryView = {
    tone: AuthenticationSummaryTone;
    title: string;
    detail?: string;
    lastAuthenticated?: string;
    tokenExpires?: string;
    apiEndpoint?: string;
    oauthConfigured: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class VisionProviderManagementViewService {
    /** Last successful complete comparison timestamp (SPA session). */
    private readonly lastSyncByProvider = new Map<string, string>();
    /** Current comparison used for row labels. Cleared when unavailable or invalidated. */
    private readonly lastSyncReportByProvider = new Map<string, VisionProviderSyncReport>();
    private readonly lastComparisonErrorByProvider = new Map<string, string>();
    private readonly refreshInFlightByProvider = new Map<string, Observable<VisionProviderSyncReport>>();

    constructor(private api: VisionProviderSubscriptionApiService) {
    }

    /**
     * Read-only EEN comparison. Dedupes in-flight requests per provider.
     * shareReplay(1) applies only to the active request, not a long-lived cache.
     */
    refreshRemoteComparison(providerId: string): Observable<VisionProviderSyncReport> {
        if (!providerId) {
            return throwError(() => new Error('Missing vision provider id.'));
        }
        const inFlight = this.refreshInFlightByProvider.get(providerId);
        if (inFlight) {
            return inFlight;
        }
        const request$ = this.api.refreshSubscriptions(providerId).pipe(
            tap((report) => this.applyRefreshReport(providerId, report)),
            catchError((error) => {
                this.markComparisonUnavailable(
                    providerId,
                    this.formatVisionSubscriptionError(error, 'Unable to refresh subscriptions.'),
                );
                return throwError(() => error);
            }),
            finalize(() => this.refreshInFlightByProvider.delete(providerId)),
            shareReplay(1),
        );
        this.refreshInFlightByProvider.set(providerId, request$);
        return request$;
    }

    isRefreshInFlight(providerId: string): boolean {
        return this.refreshInFlightByProvider.has(providerId);
    }

    isComparisonAvailable(report: VisionProviderSyncReport | null | undefined): boolean {
        if (!report) {
            return false;
        }
        return report.comparison_available === true && report.inventory_complete === true;
    }

    invalidateComparison(providerId: string): void {
        if (!providerId) {
            return;
        }
        this.lastSyncReportByProvider.delete(providerId);
        this.lastComparisonErrorByProvider.delete(providerId);
    }

    getLastComparisonError(providerId: string): string | null {
        return this.lastComparisonErrorByProvider.get(providerId) ?? null;
    }

    formatComparisonUnavailableMessage(report: VisionProviderSyncReport | null | undefined): string {
        const remoteError = this.sanitizeOperatorMessage(report?.remote_error);
        if (remoteError) {
            return remoteError;
        }
        return 'EEN remote comparison is unavailable. Rows are shown as Not compared.';
    }

    discoverProviders(): Observable<VisionProviderSummary[]> {
        return this.api.getDiagnostics().pipe(
            switchMap((bootstrap) => {
                const providerIds = [...new Set(bootstrap.supported_providers || [])];
                if (!providerIds.length) {
                    return of([]);
                }
                return forkJoin(
                    providerIds.map((providerId) =>
                        this.loadProviderSummary(providerId).pipe(
                            catchError(() => of(null)),
                        ),
                    ),
                ).pipe(
                    map((summaries) => summaries.filter((item): item is VisionProviderSummary => item !== null)),
                );
            }),
        );
    }

    loadProviderSummary(providerId: string): Observable<VisionProviderSummary> {
        return this.api.getDiagnostics(providerId).pipe(
            map((diagnostics) => this.buildProviderSummary(diagnostics)),
        );
    }

    recordSynchronization(providerId: string, report?: VisionProviderSyncReport): void {
        this.lastSyncByProvider.set(providerId, new Date().toISOString());
        this.lastComparisonErrorByProvider.delete(providerId);
        if (report) {
            this.lastSyncReportByProvider.set(providerId, report);
        }
    }

    getLastSynchronizedAt(providerId: string): string | null {
        return this.lastSyncByProvider.get(providerId) ?? null;
    }

    getLastSyncReport(providerId: string): VisionProviderSyncReport | null {
        return this.lastSyncReportByProvider.get(providerId) ?? null;
    }

    buildProviderSummary(diagnostics: VisionProviderDiagnostics): VisionProviderSummary {
        const health = this.resolveHealthStatus(diagnostics);
        return {
            provider_id: diagnostics.provider_id,
            provider_label: diagnostics.provider_label,
            health_status: health.status,
            health_label: health.label,
            oauth_configured: diagnostics.oauth_configured,
            active_subscription_count: diagnostics.active_local_subscription_count,
            last_synchronized_at: this.getLastSynchronizedAt(diagnostics.provider_id),
            diagnostics,
        };
    }

    buildDiagnosticsEntries(diagnostics: VisionProviderDiagnostics): MetadataEntry[] {
        return [
            {key: 'provider', label: 'Provider', value: diagnostics.provider_label},
            {
                key: 'environment',
                label: 'KYRA Environment',
                value: this.formatEnvironmentLabel(diagnostics.environment),
            },
            {
                key: 'environment_resolved',
                label: 'Environment Resolved',
                value: diagnostics.environment_resolved === false ? 'No' : diagnostics.environment_resolved ? 'Yes' : '—',
            },
            {
                key: 'environment_unresolved_reason',
                label: 'Unresolved Reason',
                value: diagnostics.environment_unresolved_reason || '—',
            },
            {
                key: 'een_environment',
                label: 'EEN Side',
                value: this.formatEnvironmentLabel(diagnostics.een_environment),
            },
            {key: 'oauth_configured', label: 'OAuth Configured', value: diagnostics.oauth_configured ? 'Yes' : 'No'},
            {key: 'oauth_base_url', label: 'OAuth Base URL', value: diagnostics.oauth_base_url || '—'},
            {
                key: 'oauth_token_expires',
                label: 'OAuth Token Expires',
                value: this.formatTimestamp(diagnostics.oauth_token_expires_at),
            },
            {
                key: 'public_api_base_url',
                label: 'Public API Base URL',
                value: diagnostics.public_api_base_url_configured ? 'Configured' : 'Missing',
            },
            {
                key: 'subscription_contact',
                label: 'Subscription Contact',
                value: diagnostics.subscription_contact_configured ? 'Configured' : 'Missing',
            },
            {key: 'default_webhook_url', label: 'Default Webhook URL', value: diagnostics.default_webhook_url || '—'},
            {
                key: 'active_subscriptions',
                label: 'Active Subscriptions',
                value: String(diagnostics.active_local_subscription_count),
            },
            {
                key: 'local_subscription_counts',
                label: 'Local Subscription Counts',
                value: this.formatSubscriptionCounts(diagnostics.local_subscription_counts),
            },
            {
                key: 'supported_providers',
                label: 'Supported Providers',
                value: (diagnostics.supported_providers || []).join(', ') || '—',
            },
        ];
    }

    buildAuthenticationEntries(diagnostics: VisionProviderDiagnostics): MetadataEntry[] {
        const oauthConfigured = !!diagnostics.oauth_configured;
        return [
            {
                key: 'authentication_status',
                label: 'Authentication Status',
                value: oauthConfigured ? 'Configured' : 'Not configured',
                help: 'Whether MODE A OAuth credentials are configured for this vision service.',
            },
            {
                key: 'connection_status',
                label: 'Connection Status',
                value: oauthConfigured ? 'Ready' : 'Unavailable',
                help: 'Follows oauth_configured from diagnostics. An expired access token does not clear readiness; the platform refreshes when needed.',
            },
            {
                key: 'last_successful_authentication',
                label: 'Last Successful Authentication',
                value: oauthConfigured
                    ? this.formatTimestamp(diagnostics.oauth_last_refreshed_at)
                    : 'No credential on record',
                help: 'When credentials were last refreshed successfully.',
            },
            {
                key: 'oauth_token_expires',
                label: 'OAuth Token Expires',
                value: oauthConfigured
                    ? this.formatTimestamp(diagnostics.oauth_token_expires_at)
                    : '—',
                help: 'When the current OAuth access token expires. Past expiry alone does not require reconnect while oauth_configured is true.',
            },
            {
                key: 'oauth_base_url',
                label: 'OAuth Base URL',
                value: diagnostics.oauth_base_url || '—',
                help: 'OAuth endpoint configured for this provider integration.',
            },
        ];
    }

    /**
     * Compact Authentication-page summary from diagnostics already loaded for the tab.
     * ``oauth_configured`` is the sole readiness source of truth (backend MODE A contract).
     * Access-token wall-clock expiry is diagnostic only and must not flip configured→required.
     */
    buildAuthenticationSummary(diagnostics: VisionProviderDiagnostics): AuthenticationSummaryView {
        const oauthConfigured = !!diagnostics.oauth_configured;
        const tokenExpiresRaw = diagnostics.oauth_token_expires_at || null;
        const tokenExpired = this.isTimestampInPast(tokenExpiresRaw);

        let tone: AuthenticationSummaryTone;
        let title: string;
        let detail: string | undefined;

        if (!oauthConfigured) {
            // Backend reports false for missing MODE A rows and for NEEDS_REAUTH;
            // diagnostics do not expose a separate reauth flag.
            tone = 'not_connected';
            title = 'Not Connected';
            detail = 'No usable MODE A OAuth authorization is configured. Connect or re-authenticate to continue.';
        } else {
            tone = 'connected';
            title = 'Connected';
            if (tokenExpired) {
                detail = 'Access token expiry has passed. The platform refreshes tokens when needed; reconnect only if operations report authorization failures.';
            }
        }

        const lastAuthenticated = oauthConfigured
            ? this.formatTimestampOrOmit(diagnostics.oauth_last_refreshed_at)
            : undefined;
        const tokenExpires = oauthConfigured
            ? this.formatTimestampOrOmit(tokenExpiresRaw)
            : undefined;
        const apiEndpoint = this.nonEmptyText(diagnostics.oauth_base_url);

        return {
            tone,
            title,
            detail,
            lastAuthenticated: lastAuthenticated || undefined,
            tokenExpires: tokenExpires || undefined,
            apiEndpoint: apiEndpoint || undefined,
            oauthConfigured,
        };
    }

    /**
     * Diagnostics Synchronization panel entries.
     *
     * Primary Synchronization Status is derived from the authoritative consistency
     * report. Refresh inventory ID sets remain for raw counts only and never
     * independently decide managed drift.
     */
    buildSyncEntries(
        report: VisionProviderSyncReport | null,
        lastSynchronizedAt: string | null,
        consistency?: ConsistencySyncStatusContext,
    ): MetadataEntry[] {
        const syncStatus = this.resolveManagedSynchronizationStatus(consistency);
        const entries: MetadataEntry[] = [
            {key: 'sync_status', label: 'Synchronization Status', value: syncStatus},
            {
                key: 'last_sync',
                label: 'Last successful comparison',
                value: this.formatTimestamp(lastSynchronizedAt),
            },
        ];
        if (!report || !this.isComparisonAvailable(report)) {
            return entries;
        }
        entries.push(
            {key: 'local_count', label: 'KYRA Subscriptions', value: String(report.local_count)},
            {key: 'remote_count', label: 'EEN Subscriptions', value: String(report.remote_count)},
            {
                key: 'matched_ids',
                label: 'Matched Subscription IDs',
                value: String(report.matched_subscription_ids.length),
            },
            {
                key: 'only_local',
                label: 'Not found on EEN',
                value: report.only_local_subscription_ids.join(', ') || '—',
            },
            {
                key: 'only_remote',
                label: 'EEN-only records observed during synchronization',
                value: report.only_remote_subscription_ids.join(', ') || '—',
            },
        );
        return entries;
    }

    /**
     * Managed Synchronization Status from the B4 consistency report.
     *
     * Precedence: Drift detected → Unverified → Attention required → In sync.
     * Fail closed to Unverified when evidence is missing or incomplete.
     */
    resolveManagedSynchronizationStatus(
        consistency?: ConsistencySyncStatusContext | null,
    ): string {
        if (!consistency || consistency.resolved !== true) {
            return 'Unverified';
        }
        if (consistency.loadFailed === true) {
            return 'Unverified';
        }
        const report = consistency.report;
        if (!report) {
            return 'Unverified';
        }
        if (report.mismatch === true) {
            return 'Drift detected';
        }
        if (report.unverified === true) {
            return 'Unverified';
        }
        if (report.remote_available !== true) {
            return 'Unverified';
        }
        if (report.remote_truncated === true) {
            return 'Unverified';
        }
        const counts = report.counts;
        if (!counts || typeof counts !== 'object') {
            return 'Unverified';
        }
        if (!Object.prototype.hasOwnProperty.call(counts, 'desired_missing')) {
            return 'Unverified';
        }
        const desiredMissing = Number(counts['desired_missing']);
        if (!Number.isFinite(desiredMissing)) {
            return 'Unverified';
        }
        if (desiredMissing > 0) {
            return 'Attention required';
        }
        return 'In sync';
    }

    parseListInput(value: string): string[] {
        return value
            .split(/[,\n]/)
            .map((item) => item.trim())
            .filter(Boolean);
    }

    formatTimestamp(value: string | null | undefined): string {
        return this.formatTimestampOrOmit(value) || '—';
    }

    formatStatusLabel(status: string | null | undefined): string {
        return (status || 'unknown').replace(/_/g, ' ');
    }

    formatKyraStatusLabel(status: string | null | undefined): string {
        const normalized = this.normalizeToken(status);
        if (normalized === 'active' || normalized === 'deleted' || normalized === 'disabled') {
            return normalized.toUpperCase();
        }
        const fallback = (status || 'unknown').trim() || 'unknown';
        return fallback;
    }

    formatEnvironmentLabel(value: string | null | undefined): string {
        const normalized = this.normalizeToken(value);
        if (!normalized || normalized === 'unknown') {
            return 'Unknown';
        }
        return normalized.toUpperCase();
    }

    environmentBadgeClass(value: string | null | undefined): string {
        const normalized = this.normalizeToken(value);
        const tone = !normalized || normalized === 'unknown'
            ? 'unknown'
            : ['local', 'staging', 'production'].includes(normalized)
                ? normalized
                : 'unknown';
        return `subscription-env-badge subscription-env-badge--${tone}`;
    }

    formatOrganizationLabel(
        organizationId: number | null | undefined,
        organizationName?: string | null,
    ): string {
        const name = (organizationName || '').trim();
        if (name) {
            return name;
        }
        if (organizationId === null || organizationId === undefined) {
            return 'Unassigned';
        }
        return String(organizationId);
    }

    isEnvironmentResolved(diagnostics: VisionProviderDiagnostics | null | undefined): boolean {
        return diagnostics?.environment_resolved === true;
    }

    currentEnvironment(diagnostics: VisionProviderDiagnostics | null | undefined): string {
        return this.normalizeToken(diagnostics?.environment);
    }

    isForeignSubscription(
        subscription: Pick<VisionProviderSubscription, 'environment'>,
        diagnostics: VisionProviderDiagnostics | null | undefined,
    ): boolean {
        if (!this.isEnvironmentResolved(diagnostics)) {
            return false;
        }
        const rowEnv = this.normalizeToken(subscription.environment);
        const current = this.currentEnvironment(diagnostics);
        if (!rowEnv || rowEnv === 'unknown' || !current || current === 'unknown') {
            return false;
        }
        return rowEnv !== current;
    }

    isDeletedSubscription(subscription: Pick<VisionProviderSubscription, 'status'>): boolean {
        return this.normalizeToken(subscription.status) === 'deleted';
    }

    canCreateSubscriptions(diagnostics: VisionProviderDiagnostics | null | undefined): boolean {
        return this.isEnvironmentResolved(diagnostics);
    }

    canReplaceSubscription(
        subscription: Pick<VisionProviderSubscription, 'status' | 'environment'>,
        diagnostics: VisionProviderDiagnostics | null | undefined,
    ): boolean {
        if (this.isDeletedSubscription(subscription)) {
            return false;
        }
        if (!this.isEnvironmentResolved(diagnostics)) {
            return false;
        }
        return !this.isForeignSubscription(subscription, diagnostics);
    }

    canDeleteSubscription(
        subscription: Pick<VisionProviderSubscription, 'status' | 'environment'>,
        diagnostics: VisionProviderDiagnostics | null | undefined,
    ): boolean {
        return this.canReplaceSubscription(subscription, diagnostics);
    }

    mutationDisabledReason(
        subscription: Pick<VisionProviderSubscription, 'status' | 'environment'>,
        diagnostics: VisionProviderDiagnostics | null | undefined,
    ): string | null {
        if (this.isDeletedSubscription(subscription)) {
            return 'This local record is already deleted.';
        }
        if (!this.isEnvironmentResolved(diagnostics)) {
            return diagnostics?.environment_unresolved_reason
                || 'The current KYRA environment is unresolved. Remote subscription changes are blocked.';
        }
        if (this.isForeignSubscription(subscription, diagnostics)) {
            return (
                `This subscription belongs to ${this.formatEnvironmentLabel(subscription.environment)}. ` +
                `Current environment is ${this.formatEnvironmentLabel(diagnostics?.environment)}.`
            );
        }
        return null;
    }

    remoteComparison(
        subscriptionId: string,
        report: VisionProviderSyncReport | null | undefined,
    ): VisionSubscriptionRemoteComparison {
        if (!this.isComparisonAvailable(report) || !report) {
            return 'not_compared';
        }
        if ((report.matched_subscription_ids || []).includes(subscriptionId)) {
            return 'synchronized';
        }
        if ((report.only_local_subscription_ids || []).includes(subscriptionId)) {
            return 'kyra_only';
        }
        return 'not_compared';
    }

    formatRemoteComparisonLabel(comparison: VisionSubscriptionRemoteComparison): string {
        if (comparison === 'synchronized') {
            return 'Synchronized';
        }
        if (comparison === 'kyra_only') {
            return 'Not found on EEN';
        }
        return 'Not compared';
    }

    formatRemoteComparisonHelp(comparison: VisionSubscriptionRemoteComparison): string {
        if (comparison === 'synchronized') {
            return 'This KYRA subscription ID was found in the connected Eagle Eye account.';
        }
        if (comparison === 'kyra_only') {
            return 'Subscription exists in KYRA but was not found in the connected Eagle Eye account.';
        }
        return 'No Refresh comparison is available for this row.';
    }

    formatVisionSubscriptionError(error: unknown, fallback: string): string {
        const body = this.errorBody(error);
        if (body?.['error_code'] === 'cross_environment_mutation_blocked') {
            const detail = typeof body['detail'] === 'string' ? body['detail'].trim() : '';
            return (
                detail
                || (
                    `Subscription belongs to ${this.formatEnvironmentLabel(String(body['subscription_environment'] || ''))}. ` +
                    `Current environment is ${this.formatEnvironmentLabel(String(body['current_environment'] || ''))}. ` +
                    'Cross-environment mutation is not allowed.'
                )
            );
        }
        if (this.httpStatus(error) === 409 && body?.['existing_subscription']) {
            return 'An active subscription already exists. Enable replace to update it.';
        }
        const parts: string[] = [];
        if (typeof body?.['detail'] === 'string' && body['detail'].trim()) {
            parts.push(body['detail'].trim());
        }
        const providerStatus = body?.['provider_status'];
        const providerError = typeof body?.['provider_error'] === 'string' ? body['provider_error'].trim() : '';
        if (providerStatus !== null && providerStatus !== undefined && providerStatus !== '') {
            parts.push(`Provider status ${providerStatus}`);
        }
        if (providerError && !this.looksSensitive(providerError)) {
            parts.push(providerError);
        }
        return parts.join('. ') || fallback;
    }

    private errorBody(error: unknown): Record<string, any> | null {
        if (!error || typeof error !== 'object') {
            return null;
        }
        const candidate = (error as {error?: unknown}).error;
        if (candidate && typeof candidate === 'object') {
            return candidate as Record<string, any>;
        }
        return null;
    }

    private httpStatus(error: unknown): number | null {
        if (!error || typeof error !== 'object') {
            return null;
        }
        const status = (error as {status?: unknown}).status;
        return typeof status === 'number' ? status : null;
    }

    private applyRefreshReport(providerId: string, report: VisionProviderSyncReport): void {
        if (this.isComparisonAvailable(report)) {
            this.recordSynchronization(providerId, report);
            return;
        }
        this.markComparisonUnavailable(providerId, this.formatComparisonUnavailableMessage(report));
    }

    private markComparisonUnavailable(providerId: string, message: string): void {
        this.lastSyncReportByProvider.delete(providerId);
        this.lastComparisonErrorByProvider.set(providerId, message);
    }

    private sanitizeOperatorMessage(value: string | null | undefined): string | null {
        const trimmed = (value || '').trim();
        if (!trimmed || this.looksSensitive(trimmed)) {
            return null;
        }
        return trimmed;
    }

    private looksSensitive(value: string): boolean {
        return /token|secret|authorization|bearer|password/i.test(value);
    }

    private normalizeToken(value: string | null | undefined): string {
        return (value || '').trim().toLowerCase();
    }

    private formatTimestampOrOmit(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return date.toLocaleString();
    }

    private nonEmptyText(value: string | null | undefined): string | null {
        const trimmed = (value || '').trim();
        return trimmed ? trimmed : null;
    }

    private isTimestampInPast(value: string | null | undefined): boolean {
        if (!value) {
            return false;
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return false;
        }
        return date.getTime() <= Date.now();
    }

    private resolveHealthStatus(
        diagnostics: VisionProviderDiagnostics,
    ): { status: VisionProviderSummary['health_status']; label: string } {
        if (!diagnostics.oauth_configured) {
            return {status: 'unavailable', label: 'Authentication required'};
        }
        if (!diagnostics.public_api_base_url_configured || !diagnostics.subscription_contact_configured) {
            return {status: 'degraded', label: 'Configuration incomplete'};
        }
        return {status: 'healthy', label: 'Healthy'};
    }

    private formatSubscriptionCounts(counts: Record<string, number>): string {
        const entries = Object.entries(counts || {});
        if (!entries.length) {
            return '—';
        }
        return entries.map(([status, count]) => `${status}: ${count}`).join(', ');
    }
}
